import classNames from "classnames";
import React, { DragEvent, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";

import {
  portalSlice,
  useDeletePortalFileMutation,
  useGetPortalDownloadUrlMutation,
  useGetPortalFilesQuery,
  useGetPortalMeQuery,
} from "../../../slices/PortalSlice";
import { PortalInvoicesCard } from "../components/PortalInvoicesCard";
import { PortalRequestsCard } from "../components/PortalRequestsCard";
import { PortalSetPassword } from "../components/PortalSetPassword";
import { PortalSignaturesCard } from "../components/PortalSignaturesCard";
import {
  Avatar,
  FileBadge,
  FirmBadge,
  PortalAlert,
} from "../components/PortalUi";
import { PortalClient, PortalFile } from "../portal.types";
import {
  errorMessage,
  fileProblem,
  firstName,
  formatDate,
  formatSize,
  isSignedOutError,
  PORTAL_ACCEPT,
} from "../portal.utils";
import {
  getPortalSession,
  rememberFirm,
  setPortalSession,
} from "../portalSession";
import { uploadPortalFile } from "../portalUpload";

const CLIENT_KEY = "procharted-portal-client";

type Upload = { id: number; name: string; percent: number; error?: string };

const UploadIcon = ({ size = 26 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden={"true"}>
    <path d={"M12 16V4"} />
    <path d={"m7 9 5-5 5 5"} />
    <path d={"M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"} />
  </svg>
);
const CameraIcon = () => (
  <svg
    width={"22"}
    height={"22"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden={"true"}>
    <path d={"M4 8h3l2-3h6l2 3h3v11H4z"} />
    <circle cx={"12"} cy={"13"} r={"3.5"} />
  </svg>
);
const DownloadIcon = () => (
  <svg
    width={"17"}
    height={"17"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden={"true"}>
    <path d={"M12 4v12"} />
    <path d={"m7 11 5 5 5-5"} />
    <path d={"M4 20h16"} />
  </svg>
);
const TrashIcon = () => (
  <svg
    width={"16"}
    height={"16"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden={"true"}>
    <path d={"M4 7h16"} />
    <path d={"M10 11v6M14 11v6"} />
    <path d={"M6 7l1 13h10l1-13"} />
    <path d={"M9 7V4h6v3"} />
  </svg>
);
const Chevron = () => (
  <svg
    width={"12"}
    height={"12"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"#9097A8"}
    strokeWidth={"2.4"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden={"true"}>
    <path d={"M6 9l6 6 6-6"} />
  </svg>
);

const card = "rounded-[14px] border border-[#E8E8EC] bg-white";

const FolderIcon = () => (
  <svg width={14} height={14} viewBox={"0 0 24 24"} aria-hidden={true}>
    <path
      fill={"#6B4EFF"}
      d={
        "M3 6.5A2.5 2.5 0 0 1 5.5 4h4.2c.6 0 1.2.3 1.6.8L12.6 6.5h5.9A2.5 2.5 0 0 1 21 9v8.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5z"
      }
    />
  </svg>
);

/** The client's portal: upload documents, see their uploads and what the firm shared. */
export const PortalHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const signedIn = !!getPortalSession();
  const me = useGetPortalMeQuery(undefined, { skip: !signedIn });
  const [clientId, setClientId] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(CLIENT_KEY);
    } catch {
      return null;
    }
  });
  const clients = me.data?.clients ?? [];
  const client: PortalClient | undefined =
    clients.find((c) => c.customerId === clientId) ?? clients[0];
  const files = useGetPortalFilesQuery(client?.customerId ?? "", {
    skip: !client,
  });
  const [getDownloadUrl] = useGetPortalDownloadUrlMutation();
  const [deleteFile, deleteState] = useDeletePortalFileMutation();
  const [deleting, setDeleting] = useState<PortalFile | null>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  // Chosen files wait here until "Send", so the note can still be written.
  const [pending, setPending] = useState<{ id: number; file: File }[]>([]);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");
  // Documents: which shared folder new uploads go into ("" = the firm files them).
  const [uploadFolder, setUploadFolder] = useState("");
  const [sharedFolder, setSharedFolder] = useState("");
  const [dragging, setDragging] = useState(false);
  const [message, setMessage] = useState<{
    tone: "error" | "success";
    text: string;
  } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<"shared" | "uploads">("shared");
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);

  const signOut = (notice?: string) => {
    setPortalSession(null);
    dispatch(portalSlice.util.resetApiState());
    navigate("/portal", {
      replace: true,
      state: notice ? { notice } : undefined,
    });
  };

  useEffect(() => {
    if (isSignedOutError(me.error) || isSignedOutError(files.error))
      signOut("Please sign in again.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.error, files.error]);

  useEffect(() => {
    if (client) {
      rememberFirm(client.firmName);
      try {
        window.localStorage.setItem(CLIENT_KEY, client.customerId);
      } catch {
        /* ignore */
      }
    }
  }, [client]);

  const folders = files.data?.folders ?? [];
  const uploadFolders = folders.filter((f) => f.canUpload);
  const sharedFolders = folders.filter((f) => f.fileCount > 0);
  const allSharedFiles = files.data?.shared ?? [];
  const sharedFiles = sharedFolder
    ? allSharedFiles.filter((f) => f.folderId === sharedFolder)
    : allSharedFiles;
  const uploadedFiles = files.data?.uploads ?? [];
  const newCount = allSharedFiles.filter((f) => f.isNew).length;

  if (!signedIn) return <Navigate to={"/portal"} replace={true} />;

  const addFiles = (list: FileList | File[] | null) => {
    if (!list) return;
    setMessage(null);
    const chosen = Array.from(list);
    const problems = chosen.map(fileProblem).filter((p): p is string => !!p);
    if (problems.length)
      setMessage({ tone: "error", text: problems.join(" ") });
    const ok = chosen.filter((f) => !fileProblem(f));
    setPending((current) => [
      ...current,
      ...ok.map((file) => ({ id: nextId.current++, file })),
    ]);
  };

  const sendFiles = async () => {
    if (!client || !pending.length || sending) return;
    setMessage(null);
    setSending(true);
    const batch = pending;
    setPending([]);
    let done = 0;
    for (const { id, file } of batch) {
      setUploads((u) => [...u, { id, name: file.name, percent: 0 }]);
      try {
        await uploadPortalFile(
          client.customerId,
          file,
          note,
          (percent) =>
            setUploads((u) =>
              u.map((x) => (x.id === id ? { ...x, percent } : x)),
            ),
          uploadFolder || null,
        );
        done++;
        setUploads((u) => u.filter((x) => x.id !== id));
      } catch (e) {
        if ((e as { status?: number }).status === 401) {
          setSending(false);
          return signOut("Please sign in again.");
        }
        setUploads((u) =>
          u.map((x) =>
            x.id === id ? { ...x, error: (e as Error).message } : x,
          ),
        );
      }
    }
    setSending(false);
    if (done) {
      dispatch(portalSlice.util.invalidateTags(["PortalFiles"]));
      setNote("");
      setMessage({
        tone: "success",
        text: `${done === 1 ? "1 file" : `${done} files`} sent to ${
          client.firmName
        }.`,
      });
      setMobileTab("uploads");
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const result = await deleteFile(deleting.id);
    setDeleting(null);
    if ("error" in result) {
      if (isSignedOutError(result.error))
        return signOut("Please sign in again.");
      return setMessage({ tone: "error", text: errorMessage(result.error) });
    }
    setMessage({ tone: "success", text: `"${deleting.name}" was deleted.` });
  };

  const download = async (file: PortalFile) => {
    const result = await getDownloadUrl(file.id);
    if ("error" in result) {
      if (isSignedOutError(result.error))
        return signOut("Please sign in again.");
      return setMessage({ tone: "error", text: errorMessage(result.error) });
    }
    window.location.href = result.data.url;
  };

  if (me.isLoading) {
    return (
      <div
        className={
          "flex min-h-screen items-center justify-center bg-[#F7F8FA] text-sm font-semibold text-[#6B7185]"
        }>
        Loading your portal…
      </div>
    );
  }

  const myName =
    [me.data?.firstName, me.data?.lastName].filter(Boolean).join(" ") ||
    me.data?.email ||
    "";

  if (!client) {
    return (
      <div
        className={
          "flex min-h-screen flex-col items-center justify-center gap-4 bg-[#F7F8FA] px-4 text-center font-[Inter,sans-serif]"
        }>
        <h1 className={"m-0 text-xl font-extrabold text-[#1F2433]"}>
          You don’t have access to a client portal right now
        </h1>
        <p className={"m-0 max-w-md text-sm font-medium text-[#6B7185]"}>
          {me.error
            ? errorMessage(me.error)
            : "Ask your accountant to invite you again."}
        </p>
        <button
          type={"button"}
          className={"text-sm font-bold text-[#6B4EFF]"}
          onClick={() => signOut()}>
          Sign out
        </button>
      </div>
    );
  }

  const fileRow = (file: PortalFile, kind: "upload" | "shared") => (
    <div
      key={file.id}
      className={classNames(
        "flex items-center gap-3 border-b border-[#F4F5F7] px-4 py-3.5 last:border-b-0 sm:px-6",
        kind === "shared" && file.isNew && "bg-[#FCFBFF]",
      )}>
      <FileBadge name={file.name} />
      <div className={"flex min-w-0 flex-1 flex-col gap-0.5"}>
        <span className={"truncate text-[13.5px] font-bold"} title={file.name}>
          {file.name}
        </span>
        <span className={"truncate text-[12.5px] font-medium text-[#6B7185]"}>
          {kind === "shared" ? `From ${file.by ?? client.firmName}` : file.by}
          {file.date ? ` · ${formatDate(file.date)}` : ""}
          {file.size ? ` · ${formatSize(file.size)}` : ""}
          {kind === "shared" && file.isNew ? " · New" : ""}
          {file.folderPath ? ` · in ${file.folderPath}` : ""}
        </span>
        {kind === "upload" && file.note && (
          <span
            className={
              "truncate text-[12px] font-medium italic text-[#6B7185]"
            }>
            “{file.note}”
          </span>
        )}
      </div>
      {kind === "upload" ? (
        <span
          title={
            file.canDelete
              ? `${client.firmName} hasn't opened this file yet. You can still delete it.`
              : `${client.firmName} has seen this file. To remove it, ask ${client.firmName}.`
          }
          className={
            "hidden whitespace-nowrap rounded-full bg-[#E9F9F1] px-2.5 py-1 text-xs font-bold text-[#147A4B] sm:inline"
          }>
          Received
        </span>
      ) : null}
      {kind === "upload" && file.canDelete && (
        <button
          type={"button"}
          aria-label={`Delete ${file.name}`}
          title={"Delete"}
          onClick={() => setDeleting(file)}
          className={
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#6B7185] hover:bg-[#FDECEC] hover:text-[#B42318]"
          }>
          <TrashIcon />
        </button>
      )}
      <button
        type={"button"}
        aria-label={`Download ${file.name}`}
        title={"Download"}
        onClick={() => download(file)}
        className={
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#6B7185] hover:bg-[#F4F2FE] hover:text-[#6B4EFF]"
        }>
        <DownloadIcon />
      </button>
    </div>
  );

  const sharedSection = (
    <section className={classNames(card, "flex flex-col")}>
      <div
        className={
          "flex items-center gap-2.5 border-b border-[#F0F1F4] px-4 py-4 sm:px-6 sm:py-5"
        }>
        <h2 className={"m-0 text-[17px] font-extrabold"}>Shared with you</h2>
        {newCount > 0 && (
          <span
            className={
              "rounded-full bg-[#F0EDFF] px-2.5 py-0.5 text-[11.5px] font-extrabold text-[#5B3DF5]"
            }>
            {newCount} new
          </span>
        )}
      </div>
      {sharedFolders.length > 0 && (
        <div
          className={
            "flex flex-wrap gap-2 border-b border-[#F0F1F4] px-4 py-3 sm:px-6"
          }
          aria-label={"Folders"}>
          {[{ id: "", path: "All files", fileCount: allSharedFiles.length }]
            .concat(sharedFolders)
            .map((f) => (
              <button
                key={f.id || "all"}
                type={"button"}
                onClick={() => setSharedFolder(f.id)}
                className={classNames(
                  "flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] font-bold",
                  sharedFolder === f.id
                    ? "border-[#6B4EFF] bg-[#F0EDFF] text-[#4B2FC7]"
                    : "border-[#E8E8EC] text-[#3A4256] hover:bg-[#FAF9FF]",
                )}>
                {f.id && <FolderIcon />}
                {f.path}
                <span className={"font-semibold text-[#6B7185]"}>
                  {f.fileCount}
                </span>
              </button>
            ))}
        </div>
      )}
      {files.isLoading ? (
        <div className={"px-6 py-6 text-sm text-[#6B7185]"}>Loading…</div>
      ) : sharedFiles.length ? (
        sharedFiles.map((f) => fileRow(f, "shared"))
      ) : (
        <div className={"px-6 py-6 text-[13px] font-medium text-[#6B7185]"}>
          Nothing shared yet. Files from {client.firmName} will show up here.
        </div>
      )}
    </section>
  );

  const uploadsSection = (
    <section className={classNames(card, "flex flex-col")}>
      <div
        className={
          "flex flex-wrap items-center justify-between gap-1 border-b border-[#F0F1F4] px-4 py-4 sm:px-6 sm:py-5"
        }>
        <div className={"flex flex-col gap-0.5"}>
          <h2 className={"m-0 text-[17px] font-extrabold"}>Your uploads</h2>
          <span className={"text-[12px] font-medium text-[#6B7185]"}>
            You can delete a file until {client.firmName} has seen it. After
            that, ask {client.firmName} to remove it.
          </span>
        </div>
        {client.isOrganization && (
          <span className={"text-[12.5px] font-semibold text-[#6B7185]"}>
            Everyone at {client.clientName} can see these
          </span>
        )}
      </div>
      {files.isLoading ? (
        <div className={"px-6 py-6 text-sm text-[#6B7185]"}>Loading…</div>
      ) : uploadedFiles.length ? (
        uploadedFiles.map((f) => fileRow(f, "upload"))
      ) : (
        <div className={"px-6 py-6 text-[13px] font-medium text-[#6B7185]"}>
          No uploads yet.
        </div>
      )}
    </section>
  );

  return (
    <div
      className={
        "min-h-screen bg-[#F7F8FA] font-[Inter,sans-serif] text-[#1F2433]"
      }>
      <header
        className={
          "flex h-[60px] items-center gap-2.5 border-b border-[#E8E8EC] bg-white px-4 sm:h-[68px] sm:gap-4 sm:px-10"
        }>
        <FirmBadge name={client.firmName} size={"md"} />
        <span className={"truncate text-[15px] font-extrabold sm:text-base"}>
          {client.firmName}
        </span>
        {clients.length > 1 && (
          <span className={"hidden h-6 w-px bg-[#E8E8EC] sm:block"} />
        )}
        {clients.length > 1 && (
          <div className={"relative hidden sm:block"}>
            <button
              type={"button"}
              onClick={() => setSwitcherOpen((o) => !o)}
              aria-haspopup={"listbox"}
              aria-expanded={switcherOpen}
              className={
                "inline-flex h-10 items-center gap-2 rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white pl-2 pr-3 text-[13.5px] font-bold"
              }>
              <Avatar name={client.clientName} size={26} square={true} />
              {client.clientName}
              <Chevron />
            </button>
            {switcherOpen && (
              <ul
                role={"listbox"}
                className={
                  "absolute left-0 top-12 z-20 m-0 min-w-[240px] list-none rounded-xl border border-[#EDEFF3] bg-white p-1.5 shadow-[0_12px_32px_rgba(31,36,51,0.14)]"
                }>
                {clients.map((c) => (
                  <li key={c.customerId}>
                    <button
                      type={"button"}
                      role={"option"}
                      aria-selected={c.customerId === client.customerId}
                      onClick={() => {
                        setClientId(c.customerId);
                        setSwitcherOpen(false);
                        setMessage(null);
                      }}
                      className={classNames(
                        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13.5px] font-bold hover:bg-[#F7F8FA]",
                        c.customerId === client.customerId && "bg-[#F4F2FE]",
                      )}>
                      <Avatar name={c.clientName} size={26} square={true} />
                      <span className={"flex flex-col"}>
                        {c.clientName}
                        <span
                          className={
                            "text-[11.5px] font-semibold text-[#6B7185]"
                          }>
                          {c.firmName}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <span className={"flex-1"} />
        <div className={"relative"}>
          <button
            type={"button"}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup={"menu"}
            aria-expanded={menuOpen}
            className={"flex items-center gap-2.5"}>
            <Avatar name={myName} size={34} />
            <span className={"hidden text-[13.5px] font-bold sm:inline"}>
              {myName}
            </span>
            <Chevron />
          </button>
          {menuOpen && (
            <div
              role={"menu"}
              className={
                "absolute right-0 top-11 z-20 w-56 rounded-xl border border-[#EDEFF3] bg-white p-1.5 shadow-[0_12px_32px_rgba(31,36,51,0.14)]"
              }>
              <div
                className={
                  "truncate px-2.5 py-2 text-xs font-semibold text-[#6B7185]"
                }>
                {me.data?.email}
              </div>
              <button
                type={"button"}
                role={"menuitem"}
                className={
                  "w-full rounded-lg px-2.5 py-2 text-left text-[13.5px] font-bold hover:bg-[#F7F8FA]"
                }
                onClick={() => {
                  setMenuOpen(false);
                  setPasswordOpen(true);
                }}>
                {me.data?.hasPassword ? "Change password" : "Set a password"}
              </button>
              <button
                type={"button"}
                role={"menuitem"}
                className={
                  "w-full rounded-lg px-2.5 py-2 text-left text-[13.5px] font-bold hover:bg-[#F7F8FA]"
                }
                onClick={() => signOut()}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <main
        className={
          "mx-auto flex max-w-[1280px] flex-col gap-4 px-4 py-5 sm:gap-6 sm:px-10 sm:pb-12 sm:pt-9"
        }>
        {clients.length > 1 && (
          <select
            aria-label={"Client"}
            value={client.customerId}
            onChange={(e) => setClientId(e.target.value)}
            className={
              "h-12 rounded-[10px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-sm font-bold sm:hidden"
            }>
            {clients.map((c) => (
              <option key={c.customerId} value={c.customerId}>
                {c.clientName}
              </option>
            ))}
          </select>
        )}
        <div className={"hidden flex-col gap-1.5 sm:flex"}>
          <h1 className={"m-0 text-[26px] font-extrabold tracking-[-0.4px]"}>
            Hi {firstName(me.data?.firstName, me.data?.email)}
          </h1>
          <span className={"text-sm font-medium text-[#6B7185]"}>
            Files you upload go straight to {client.firmName} for{" "}
            {client.clientName}.
          </span>
        </div>

        {message && (
          <PortalAlert tone={message.tone}>{message.text}</PortalAlert>
        )}

        <PortalRequestsCard customerId={client.customerId} />
        <PortalSignaturesCard customerId={client.customerId} />
        <PortalInvoicesCard
          customerId={client.customerId}
          onError={(text) => setMessage({ tone: "error", text })}
        />

        <div
          className={
            "grid grid-cols-1 items-start gap-4 sm:gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]"
          }>
          <div className={"flex flex-col gap-4 sm:gap-6"}>
            <section
              className={classNames(
                card,
                "flex flex-col gap-3 p-[18px] sm:gap-4 sm:p-6",
              )}>
              <h2 className={"m-0 text-base font-extrabold sm:text-[17px]"}>
                Upload documents
              </h2>
              <form className={"hidden"}>
                <input
                  ref={fileInput}
                  type={"file"}
                  multiple={true}
                  accept={PORTAL_ACCEPT}
                  className={"hidden"}
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.currentTarget.form?.reset();
                  }}
                />
              </form>
              <form className={"hidden"}>
                <input
                  ref={cameraInput}
                  type={"file"}
                  accept={"image/*"}
                  capture={"environment"}
                  className={"hidden"}
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.currentTarget.form?.reset();
                  }}
                />
              </form>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={classNames(
                  "hidden flex-col items-center gap-3 rounded-xl border-2 border-dashed px-6 py-9 text-center sm:flex",
                  dragging
                    ? "border-[#6B4EFF] bg-[#F1EEFC]"
                    : "border-[#C9BFFB] bg-[#FAF9FF]",
                )}>
                <span
                  className={
                    "flex h-14 w-14 items-center justify-center rounded-full bg-[#EBE6FD] text-[#6B4EFF]"
                  }>
                  <UploadIcon />
                </span>
                <span className={"text-[15px] font-extrabold"}>
                  Drag files here
                </span>
                <span className={"text-[13px] font-medium text-[#6B7185]"}>
                  PDF, photos, Word or Excel · up to 50 MB each
                </span>
                <button
                  type={"button"}
                  onClick={() => fileInput.current?.click()}
                  className={
                    "h-[42px] rounded-lg bg-[#6B4EFF] px-[22px] text-[13.5px] font-extrabold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)] hover:bg-[#5B3DF5]"
                  }>
                  Choose files
                </button>
              </div>
              <div className={"grid grid-cols-2 gap-2.5 sm:hidden"}>
                <button
                  type={"button"}
                  onClick={() => cameraInput.current?.click()}
                  className={
                    "flex h-[84px] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-[#C9BFFB] bg-[#FAF9FF] text-[13.5px] font-extrabold text-[#4B2FC7]"
                  }>
                  <CameraIcon />
                  Take a photo
                </button>
                <button
                  type={"button"}
                  onClick={() => fileInput.current?.click()}
                  className={
                    "flex h-[84px] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-[#C9BFFB] bg-[#FAF9FF] text-[13.5px] font-extrabold text-[#4B2FC7]"
                  }>
                  <UploadIcon size={22} />
                  Choose files
                </button>
              </div>
              {pending.length > 0 && (
                <div className={"flex flex-col gap-2"}>
                  <span className={"text-[13px] font-bold text-[#3A4256]"}>
                    Ready to send
                  </span>
                  {pending.map(({ id, file }) => (
                    <div
                      key={id}
                      className={
                        "flex items-center gap-3 rounded-[10px] border border-[#E8E8EC] px-3.5 py-2.5"
                      }>
                      <FileBadge name={file.name} />
                      <span className={"flex min-w-0 flex-1 flex-col"}>
                        <span className={"truncate text-[13px] font-bold"}>
                          {file.name}
                        </span>
                        <span className={"text-xs font-medium text-[#6B7185]"}>
                          {formatSize(file.size)}
                        </span>
                      </span>
                      <button
                        type={"button"}
                        aria-label={`Remove ${file.name}`}
                        title={"Remove"}
                        className={
                          "rounded-md px-1.5 text-[#9097A8] hover:bg-[#F1F2F5] hover:text-[#B42318]"
                        }
                        onClick={() =>
                          setPending((list) => list.filter((x) => x.id !== id))
                        }>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className={"flex flex-col gap-1.5"}>
                <label
                  htmlFor={"portal-note"}
                  className={"text-[13px] font-bold text-[#3A4256]"}>
                  Note for your accountant (optional)
                </label>
                <input
                  id={"portal-note"}
                  value={note}
                  maxLength={1000}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={"e.g. Here are the March bank statements"}
                  className={
                    "h-[42px] rounded-[9px] border-[1.5px] border-[#DCE2EA] px-3.5 text-[13.5px] outline-none placeholder:text-[#9097A8] focus:border-[#6B4EFF]"
                  }
                />
              </div>
              {uploadFolders.length > 0 && (
                <div className={"flex flex-col gap-1.5"}>
                  <label
                    htmlFor={"portal-folder"}
                    className={"text-[13px] font-bold text-[#3A4256]"}>
                    Put them in
                  </label>
                  <select
                    id={"portal-folder"}
                    value={uploadFolder}
                    onChange={(e) => setUploadFolder(e.target.value)}
                    className={
                      "h-[42px] rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-[13.5px] outline-none focus:border-[#6B4EFF]"
                    }>
                    <option value={""}>Let {client.firmName} file them</option>
                    {uploadFolders.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.path}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type={"button"}
                onClick={sendFiles}
                disabled={!pending.length || sending}
                className={
                  "h-[44px] rounded-[9px] bg-[#6B4EFF] text-[14px] font-extrabold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)] hover:bg-[#5B3DF5] disabled:cursor-not-allowed disabled:bg-[#C9BFFB] disabled:shadow-none"
                }>
                {sending
                  ? "Sending…"
                  : pending.length
                  ? `Send ${
                      pending.length === 1
                        ? "1 file"
                        : `${pending.length} files`
                    } to ${client.firmName}`
                  : "Choose files to send"}
              </button>
              {uploads.map((u) => (
                <div
                  key={u.id}
                  className={
                    "flex items-center gap-3 rounded-[10px] border border-[#E8E8EC] px-3.5 py-3"
                  }>
                  <FileBadge name={u.name} />
                  <div className={"flex min-w-0 flex-1 flex-col gap-1.5"}>
                    <div
                      className={
                        "flex justify-between gap-2 text-[13px] font-bold"
                      }>
                      <span className={"truncate"}>{u.name}</span>
                      {!u.error && (
                        <span className={"text-[#6B7185]"}>{u.percent}%</span>
                      )}
                    </div>
                    {u.error ? (
                      <span
                        className={
                          "text-[12.5px] font-semibold text-[#B42318]"
                        }>
                        {u.error}
                      </span>
                    ) : (
                      <span className={"block h-1.5 rounded bg-[#EEF0F4]"}>
                        <span
                          className={
                            "block h-1.5 rounded bg-[#6B4EFF] transition-all"
                          }
                          style={{ width: `${u.percent}%` }}
                        />
                      </span>
                    )}
                  </div>
                  {u.error && (
                    <button
                      type={"button"}
                      aria-label={"Dismiss"}
                      className={"text-[#6B7185]"}
                      onClick={() =>
                        setUploads((x) => x.filter((y) => y.id !== u.id))
                      }>
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </section>

            <div
              className={
                "grid grid-cols-2 gap-1 rounded-[10px] bg-[#EDEEF2] p-1 lg:hidden"
              }
              role={"tablist"}>
              {(["shared", "uploads"] as const).map((t) => (
                <button
                  key={t}
                  type={"button"}
                  role={"tab"}
                  aria-selected={mobileTab === t}
                  onClick={() => setMobileTab(t)}
                  className={classNames(
                    "h-10 rounded-lg text-[13.5px]",
                    mobileTab === t
                      ? "bg-white font-extrabold shadow-[0_1px_3px_rgba(31,36,51,0.12)]"
                      : "font-bold text-[#6B7185]",
                  )}>
                  {t === "shared"
                    ? `Shared with me${newCount ? ` · ${newCount}` : ""}`
                    : "My uploads"}
                </button>
              ))}
            </div>
            <div
              className={classNames(
                "lg:block",
                mobileTab === "uploads" ? "block" : "hidden",
              )}>
              {uploadsSection}
            </div>
          </div>

          <div className={"flex flex-col gap-4 sm:gap-6"}>
            <div
              className={classNames(
                "lg:block",
                mobileTab === "shared" ? "block" : "hidden",
              )}>
              {sharedSection}
            </div>
          </div>
        </div>
      </main>

      {deleting && (
        <div
          className={
            "fixed inset-0 z-50 flex items-center justify-center bg-[rgba(31,36,51,0.45)] px-4"
          }
          role={"dialog"}
          aria-modal={"true"}
          aria-label={"Delete file"}>
          <div
            className={
              "flex w-full max-w-[420px] flex-col gap-4 rounded-2xl bg-white p-6 shadow-[0_20px_60px_rgba(31,36,51,0.25)]"
            }>
            <h2 className={"m-0 text-xl font-extrabold"}>Delete this file?</h2>
            <span className={"text-sm leading-relaxed text-[#3A4256]"}>
              “{deleting.name}” will be removed, and {client.firmName} won’t
              receive it. You can upload the right file afterwards.
            </span>
            <div className={"flex gap-2.5"}>
              <button
                type={"button"}
                onClick={() => setDeleting(null)}
                className={
                  "h-[44px] flex-1 rounded-[9px] border-[1.5px] border-[#DCE2EA] text-sm font-bold text-[#3A4256]"
                }>
                Cancel
              </button>
              <button
                type={"button"}
                onClick={confirmDelete}
                disabled={deleteState.isLoading}
                className={
                  "h-[44px] flex-1 rounded-[9px] bg-[#D92D20] text-sm font-extrabold text-white hover:bg-[#B42318] disabled:opacity-60"
                }>
                {deleteState.isLoading ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordOpen && (
        <PortalSetPassword
          hasPassword={!!me.data?.hasPassword}
          onClose={() => setPasswordOpen(false)}
        />
      )}
    </div>
  );
};
